// store/metrics/sliceMetrics.ts
import type { TCreateSlice, TAsyncState } from "@/store";

// dependencies types
import type { TUserInfo } from "@/apis/odin/apiODIN";
import type { TApiUserpilot } from "@/apis/userpilot/apiUserpilot";
import type { TApiDatadog } from "@/apis/datadog/apiDatadog";

type TStateMetrics = {
    datadogInitialization: TAsyncState<null, Error>;
    userpilotInitialization: TAsyncState<null, Error>;
};

type TActionsPublicMetrics = {
    resetStateMetrics: () => void;
    setDatadogUser: () => void;
    initializeUserpilot: () => void;
};

type TActionsPrivateMetrics = {
    setDatadogInitializationPending: () => void;
    setDatadogInitializationSuccess: () => void;
    setDatadogInitializationError: (error: Error) => void;
    setUserpilotInitializationPending: () => void;
    setUserpilotInitializationSuccess: () => void;
    setUserpilotInitializationError: (error: Error) => void;
};

export type TSliceMetrics = TStateMetrics & TActionsPublicMetrics;

type TSliceDependenciesMetrics = {
    apiDatadog: TApiDatadog;
    apiUserpilot: TApiUserpilot;
    getUserInfo: () => TUserInfo | null;
};

export const initialStateMetrics = {
    datadogInitialization: {
        status: "idle",
        data: null,
        error: null,
    },
    userpilotInitialization: {
        status: "idle",
        data: null,
        error: null,
    },
} as const satisfies TStateMetrics;

export const createSliceMetrics: TCreateSlice<
    TSliceMetrics,
    TSliceDependenciesMetrics
> =
    ({ getUserInfo, apiDatadog, apiUserpilot }) =>
    (set, get) => {
        const actionsPrivate: TActionsPrivateMetrics = {
            setUserpilotInitializationPending: () => {
                set(
                    {
                        userpilotInitialization: {
                            status: "pending",
                            data: null,
                            error: null,
                        },
                    },
                    false,
                    "userpilot/setUserpilotInitializationPending",
                );
            },
            setUserpilotInitializationSuccess: () => {
                set(
                    {
                        userpilotInitialization: {
                            status: "resolved",
                            data: null,
                            error: null,
                        },
                    },
                    false,
                    "userpilot/setUserpilotInitializationSuccess",
                );
            },
            setUserpilotInitializationError: (error: Error) => {
                set(
                    {
                        userpilotInitialization: {
                            status: "rejected",
                            data: null,
                            error,
                        },
                    },
                    false,
                    "userpilot/setUserpilotInitializationError",
                );
            },
            setDatadogInitializationPending: () => {
                set(
                    {
                        datadogInitialization: {
                            status: "pending",
                            data: null,
                            error: null,
                        },
                    },
                    false,
                    "metrics/setDatadogInitializationPending",
                );
            },
            setDatadogInitializationSuccess: () => {
                set(
                    {
                        datadogInitialization: {
                            status: "resolved",
                            data: null,
                            error: null,
                        },
                    },
                    false,
                    "metrics/setDatadogInitializationSuccess",
                );
            },
            setDatadogInitializationError: (error: Error) => {
                set(
                    {
                        datadogInitialization: {
                            status: "rejected",
                            data: null,
                            error,
                        },
                    },
                    false,
                    "metrics/setDatadogInitializationError",
                );
            },
        };

        const actionsPublic: TActionsPublicMetrics = {
            resetStateMetrics: () => {
                set(initialStateMetrics, false, "metrics/resetState");
            },
            setDatadogUser: () => {
                const currentStatus = get().datadogInitialization.status;
                if (currentStatus === "resolved") return;
                if (currentStatus === "pending") return;

                actionsPrivate.setDatadogInitializationPending();
                const userInfo = getUserInfo();
                if (!userInfo) return;

                try {
                    apiDatadog.setUser({
                        id: userInfo.userId.toString(),
                        companyId: userInfo.companyId,
                        companyName: userInfo.companyName,
                        email: userInfo.email,
                    });
                    actionsPrivate.setDatadogInitializationSuccess();
                } catch (error) {
                    actionsPrivate.setDatadogInitializationError(
                        error as Error,
                    );
                }
            },
            initializeUserpilot: () => {
                const currentStatus = get().userpilotInitialization.status;
                if (currentStatus === "resolved") return;
                if (currentStatus === "pending") return;

                actionsPrivate.setUserpilotInitializationPending();
                const userInfo = getUserInfo();
                if (!userInfo) return;

                try {
                    apiUserpilot.initUserpilot();
                    apiUserpilot.setUser({
                        id: userInfo.userId.toString(),
                        companyId: userInfo.companyId,
                        companyName: userInfo.companyName,
                        userEmail: userInfo.email,
                        isProdaUser: userInfo.isProdaUser,
                        isGlobalAdmin: userInfo.isGlobalAdmin,
                    });
                    actionsPrivate.setUserpilotInitializationSuccess();
                } catch (error) {
                    actionsPrivate.setUserpilotInitializationError(
                        error as Error,
                    );
                }
            },
        };

        return {
            ...initialStateMetrics,
            ...actionsPublic,
        };
    };
